import {
  ArrowRight,
  Bell,
  Briefcase,
  type LucideIcon,
  Mail,
  MessageSquare,
  Search,
  Upload,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { useNotificationHub } from '@/modules/notifications/hooks/useNotificationHub';
import { useReadAllNotificationsMutation } from '@/modules/notifications/mutations/useReadAllNotificationsMutation';
import { useReadNotificationMutation } from '@/modules/notifications/mutations/useReadNotificationMutation';
import { useNotificationsQuery } from '@/modules/notifications/queries/useNotificationsQuery';
import type {
  NotificationEntityAction,
  NotificationsApi,
} from '@/modules/notifications/types';
import { portalNotificationLink } from '@/modules/notifications/utils/portalNotificationLink';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { actionErrorMessage } from '@/utils/actionErrorMessage';
import { formatRelativeTime } from '@/utils/dateUtils';

/**
 * What each notification is about, and the call to action its link gets.
 * Job creation has no action label: the row itself opens the job.
 */
const actionDisplay: Record<
  NotificationEntityAction,
  { icon: LucideIcon; action?: string }
> = {
  JobCreated: { icon: Briefcase },
  SourcingStarted: { icon: Search, action: 'View job' },
  SourcingFinished: { icon: Search, action: 'View job' },
  SourcingFailed: { icon: Search, action: 'View job' },
  ManualUploadFinished: { icon: Upload, action: 'View job' },
  ManualUploadFailed: { icon: Upload, action: 'View job' },
  OutreachGenerated: { icon: Mail, action: 'Review drafts' },
  NewMessage: { icon: MessageSquare, action: 'View messages' },
  MessageReviewRequired: { icon: MessageSquare, action: 'View messages' },
  JobImportSuccess: { icon: Briefcase, action: 'View jobs' },
  JobImportFailed: { icon: Briefcase, action: 'View jobs' },
};

/** Strong ease-out, the same curve as the portal's list rows (index.css). */
const easeOut = 'ease-[cubic-bezier(0.23,1,0.32,1)]';

/**
 * The portal's notification bell. It also owns the hub connection, so live
 * notifications refresh the portal's data while any portal page is open.
 */
export function PortalNotifications() {
  useNotificationHub();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const notificationsQuery = useNotificationsQuery();
  const readMutation = useReadNotificationMutation();
  const readAllMutation = useReadAllNotificationsMutation();

  const notifications = notificationsQuery.data ?? [];
  const unreadCount = notifications.filter(
    (notification) => !notification.readUtc,
  ).length;

  // Opening a notification marks it read on the side; the user came for the
  // link, so a failed mark stays silent and the dot simply comes back.
  const read = (notification: NotificationsApi) => {
    if (!notification.readUtc) readMutation.mutate(notification.id);
  };

  // The dots clearing is the confirmation. They clear before the server
  // answers, so a failure brings them back and needs saying.
  const readAll = () =>
    readAllMutation.mutate(undefined, {
      onError: (error) =>
        toast.error(actionErrorMessage('mark notifications as read', error)),
    });

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : 'Notifications'
          }
          className="hover:bg-accent hover:text-foreground focus-visible:ring-ring/50 data-[state=open]:bg-muted data-[state=open]:text-foreground relative ml-auto grid size-8 cursor-pointer place-items-center rounded-lg text-neutral-500 transition-[background-color,color,scale] duration-150 ease-out outline-none focus-visible:ring-[3px] active:scale-[0.97]"
          type="button"
        >
          <Bell className="size-[18px]" />
          {unreadCount > 0 && (
            // Enters once, when the first unread notification arrives.
            <span
              className={cn(
                'bg-primary text-primary-foreground ring-background absolute top-[3px] right-px grid h-3.5 min-w-3.5 place-items-center rounded-full px-[3px] text-[9px] leading-none font-bold tabular-nums ring-2',
                'transition-[opacity,scale] duration-200 starting:scale-90 starting:opacity-0 motion-reduce:starting:scale-100',
                easeOut,
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn(
          'w-[calc(100vw-2rem)] max-w-[400px] overflow-hidden rounded-xl p-0 shadow-[0_12px_32px_#18181c24]',
          // Opens quickly, closes quicker; reduced motion keeps only the fade.
          'duration-180 data-[state=closed]:duration-120',
          'motion-reduce:slide-in-from-top-0! motion-reduce:zoom-in-100! motion-reduce:zoom-out-100!',
          easeOut,
        )}
        // Radix would focus "Mark all as read", where a stray Enter marks
        // everything read. Focus the panel; Tab reaches the actions.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          panelRef.current?.focus();
        }}
        ref={panelRef}
        sideOffset={8}
      >
        <div className="flex items-center justify-between border-b px-4 py-3.5">
          <h2 className="text-sm font-semibold">Notifications</h2>
          {unreadCount > 0 && (
            <button
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 active:bg-accent -my-2 -mr-2 cursor-pointer rounded-md p-2 text-xs font-medium transition-colors duration-100 outline-none focus-visible:ring-[3px]"
              onClick={readAll}
              type="button"
            >
              Mark all as read
            </button>
          )}
        </div>
        <div className="max-h-[min(420px,70vh)] overflow-y-auto">
          {notificationsQuery.isLoading ? (
            <div aria-label="Loading notifications" className="divide-y">
              {[0, 1, 2].map((row) => (
                <div className="flex gap-3 px-4 py-3.5" key={row}>
                  <Skeleton className="size-[30px] shrink-0 rounded-lg" />
                  <div className="flex flex-1 flex-col gap-2 pt-1">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : notificationsQuery.isError ? (
            <p className="px-4 py-8 text-center text-[13px] text-neutral-500">
              Notifications could not be loaded. Reopen this in a moment.
            </p>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
              <span className="bg-muted text-muted-foreground grid size-10 place-items-center rounded-xl">
                <Bell aria-hidden className="size-[18px]" />
              </span>
              <div className="flex flex-col gap-1">
                <p className="text-[13px] font-semibold">
                  You&apos;re all caught up
                </p>
                <p className="text-muted-foreground text-xs">
                  Finlay lets you know here when drafts are ready or a candidate
                  replies.
                </p>
              </div>
            </div>
          ) : (
            <ul className="divide-y">
              {notifications.map((notification) => (
                <NotificationRow
                  href={portalNotificationLink(notification, searchParams)}
                  key={notification.id}
                  notification={notification}
                  onOpen={() => {
                    read(notification);
                    setIsOpen(false);
                  }}
                  onRead={() => read(notification)}
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationRow({
  notification,
  href,
  onOpen,
  onRead,
}: {
  notification: NotificationsApi;
  href: string | undefined;
  onOpen: () => void;
  onRead: () => void;
}) {
  const isUnread = !notification.readUtc;
  const display = notification.entityAction
    ? actionDisplay[notification.entityAction]
    : undefined;
  const Icon = display?.icon ?? Bell;
  const action = href ? display?.action : undefined;
  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          'bg-muted grid size-[30px] shrink-0 place-items-center rounded-lg transition-colors duration-200 ease-[ease]',
          'group-hover:bg-background group-focus-visible:bg-background',
          isUnread ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        <Icon className="size-[15px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span
          className={cn(
            'text-foreground text-[13px] leading-[1.4]',
            isUnread ? 'font-semibold' : 'font-medium',
          )}
        >
          {notification.title}
          {isUnread && <span className="sr-only"> (unread)</span>}
        </span>
        {notification.content && (
          <span className="text-muted-foreground text-xs">
            {notification.content}
          </span>
        )}
        <span className="text-input-placeholder flex items-center gap-2 pt-[3px] text-xs">
          {formatRelativeTime(notification.createdAt)}
          {action && (
            <>
              <span aria-hidden>·</span>
              <span className="text-primary inline-flex items-center gap-[3px] font-medium">
                {action}
                <ArrowRight className="size-3" />
              </span>
            </>
          )}
        </span>
      </span>
      {/* Always in place, so marking read fades it out instead of popping. */}
      <span
        aria-hidden
        className={cn(
          'bg-primary size-2 shrink-0 rounded-full transition-opacity duration-150 ease-out',
          !isUnread && 'opacity-0',
        )}
      />
    </>
  );
  const className =
    'flex w-full gap-3 px-4 py-3.5 text-left transition-colors duration-200 ease-[ease]';
  // Only rows that do something highlight; unread is the dot and bold title.
  // The hover tint eases in; the press snaps, so it answers the click.
  const interactive =
    'group hover:bg-secondary-150 focus-visible:bg-secondary-150 active:bg-primary/15 active:duration-75';

  return (
    <li>
      {href ? (
        <Link
          className={cn(className, interactive, 'outline-none')}
          onClick={onOpen}
          to={href}
        >
          {body}
        </Link>
      ) : isUnread ? (
        // Without a destination, clicking is the only way to mark this one read.
        <button
          className={cn(className, interactive, 'cursor-pointer outline-none')}
          onClick={onRead}
          type="button"
        >
          {body}
        </button>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
}
